import {numberFormatterWithDecimalLimit} from "./formUtils";
import { canUpdateTransactionTeamLead } from './authUtils';

/**
 * Determines whether a transaction team can currently be edited, based on the team's status. New (unsaved) transactions and, when in approval context, "Pending" teams are editable; "Formed", "Declined", and "Historic" teams are not.
 *
 * @param txId - The transaction id; `'0'` indicates a new, unsaved transaction.
 * @param txTeamStatus - The current transaction team status.
 * @param isApproval - Whether this check is being made in an approval context, which allows editing while the team is 'Pending'.
 * @returns `true` if the team can be edited.
 */
export const canEdit = (txId: string | undefined, txTeamStatus: string, isApproval = false) => {
    if (txId === '0') return true;
    if (txTeamStatus === 'Pending' && !isApproval) return false;
    if (['Formed', 'Declined', 'Historic'].includes(txTeamStatus)) return false;
    return true;
};

const teamFormationConstants = {
  canUpdateLeadPhase: 'pipeline',
  canUpdateLeadMilestones: ['team', 'prelimanalysis']
}

/**
 * Determines whether the current user can update the transaction lead field, which is only permitted during the 'pipeline' phase and while on the 'team' or 'prelimanalysis' milestone, and only for users holding the transaction-team-lead role.
 *
 * @param phaseValue - The transaction's current phase.
 * @param milestoneValue - The transaction's current milestone.
 * @param userRoles - The current user's role keys.
 * @returns `true` if the user is allowed to update the transaction lead.
 */
export const canUpdateTransactionLead = (phaseValue: string, milestoneValue: string, userRoles: string[]) => {
  return canUpdateTransactionTeamLead(userRoles)
    && phaseValue === teamFormationConstants.canUpdateLeadPhase
    && teamFormationConstants.canUpdateLeadMilestones.includes(milestoneValue);
}

/**
 * Configuration array defining the fields displayed in the acquisition summary table, each with a key, title, type, and optional formatter/parser for display.
 */
export const acquisitionSummaryFields = [
    {
        key: 'seller',
        title: 'Seller',
        type: 'string',
        prefix: '',
        formatter: undefined,
        parser: undefined
    },
    {
        key: 'location',
        title: 'Location',
        type: 'string',
        prefix: '',
        formatter: undefined,
        parser: undefined
    },
    {
        key: 'nearestFRIProperty',
        title: 'Nearest FRI Property',
        type: 'string',
        prefix: '',
        formatter: undefined,
        parser: undefined
    },
    {
        key: 'crops',
        title: 'Crops',
        type: 'string',
        prefix: '',
        formatter: undefined,
        parser: undefined
    },
    {
        key: 'grossAcres',
        title: 'Gross Acres',
        type: 'number',
        prefix: '',
        formatter: numberFormatterWithDecimalLimit(2),
        parser: (value: any) => value!.replace(/\$\s?|(,*)/g, '')
    },
    {
        key: 'tillableAcres',
        title: 'Tillable Acres',
        type: 'number',
        prefix: '',
        formatter: numberFormatterWithDecimalLimit(2),
        parser: (value: any) => value!.replace(/\$\s?|(,*)/g, '')
    },
    {
        key: 'askingPrice',
        title: 'Asking Price',
        type: 'number',
        prefix: '$',
        formatter: numberFormatterWithDecimalLimit(0),
        parser: (value: any) => Math.floor(+value.replace(/\$\s*/g, '').replace(/,/g, ''))
    },
    {
        key: 'suggestedOfferPrice',
        title: 'Suggested Offer Price',
        type: 'number',
        prefix: '$',
        formatter: numberFormatterWithDecimalLimit(0),
        parser: (value: any) => Math.floor(+value.replace(/\$\s*/g, '').replace(/,/g, ''))
    },
    {
        key: 'approvedPrice',
        title: 'Approved Price',
        type: 'number',
        prefix: '$',
        formatter: numberFormatterWithDecimalLimit(0),
        parser: (value: any) => Math.floor(+value.replace(/\$\s*/g, '').replace(/,/g, ''))
    },
    {
        key: 'pricePerTillableAcre',
        title: 'Price Per Tillable Acre',
        type: 'number',
        prefix: '$',
        formatter: numberFormatterWithDecimalLimit(2),
        parser: (value: any) => value.replace(/^\$\s*/, '').replace(/,/g, '')
    },
    {
        key: 'irr',
        title: 'IRR',
        type: 'number',
        prefix: '',
        formatter: (value: string) => `${numberFormatterWithDecimalLimit(3)(value)}%`,
        parser: (value: string) => value!.replace('%', '')
    },
    {
        key: 'coc',
        title: 'COC',
        type: 'number',
        prefix: '',
        formatter: (value: string) => `${numberFormatterWithDecimalLimit(3)(value)}%`,
        parser: (value: string) => value!.replace(/%/g, '')
    }
];
